import { BaseElement } from 'base';
import { bus } from 'bus';
import { SEARCH_TOPIC } from 'search-topic';

const TOPIC = 'desktop-menu:active';
const HOVER_DELAY = 200;
const DESKTOP = '(min-width: 1200px)';
const LOCK_CLASS = 'desktop-menu-open';
const TAB_SELECTOR = '[data-desktop-menu-tab]';
const TOP_ITEM = '.desktop-menu__item > .menu-section';

class DesktopMenu extends BaseElement {
  #active = null;
  #openedBy = null;
  #timer = 0;

  onConnect() {
    this.cleanup(() => clearTimeout(this.#timer));
    this.subscribe(TOPIC, this.#render);
    this.subscribeLive('cart-drawer:active', this.#closeWhenActive);
    this.subscribeLive('slide-cart:active', this.#closeWhenActive);
    this.subscribeLive(SEARCH_TOPIC, this.#closeWhenActive);

    this.listen(this, 'click', this.#onClick);
    this.listen(this, 'pointerdown', this.#onPointerDown);
    this.listen(this, 'pointerover', this.#onPointerOver);
    this.listen(this, 'pointerleave', this.#onPointerLeave);
    this.listen(this, 'focusout', this.#onFocusOut);
    this.listen(document, 'click', this.#onDocumentClick);
    this.listen(document, 'keydown', this.#onKeydown);
    this.listen(window, 'resize', this.#measure);
    this.listen(window.matchMedia(DESKTOP), 'change', event => {
      if (!event.matches) this.#closeOwn();
    });
  }

  onDisconnect() {
    if (this.#owns(this.#active)) bus.publish(TOPIC, null);
  }

  #owns(active) {
    return (
      typeof active === 'string' &&
      active.startsWith(`${this.dataset.menuId}-panel-`)
    );
  }

  #tabFor(panelId) {
    return this.qa(TAB_SELECTOR).find(
      tabButton => tabButton.getAttribute('aria-controls') === panelId
    );
  }

  #panel(panelId) {
    return panelId ? this.q(`#${CSS.escape(panelId)}`) : null;
  }

  #render(active) {
    this.#active = active;
    const mine = this.#owns(active);
    if (!mine) this.#openedBy = null;

    this.qa(TAB_SELECTOR).forEach(tabButton => {
      const open = mine && tabButton.getAttribute('aria-controls') === active;
      tabButton.setAttribute('aria-expanded', String(open));
      const panel = this.#panel(tabButton.getAttribute('aria-controls'));
      if (panel) panel.hidden = !open;
    });
    this.dataset.open = String(mine);
    const scrim = this.q('[data-desktop-menu-scrim]');
    if (scrim) scrim.hidden = !mine;

    if (mine) {
      document.documentElement.classList.add(LOCK_CLASS);
      this.#measure();
    } else if (active == null) {
      document.documentElement.classList.remove(LOCK_CLASS);
    }
  }

  #measure() {
    if (!this.#owns(this.#active)) return;
    const panel = this.#panel(this.#active);
    if (!panel) return;
    this.style.setProperty(
      '--desktop-menu-top',
      `${panel.getBoundingClientRect().top}px`
    );
  }

  #closeOwn() {
    this.#cancel();
    if (this.#owns(this.#active)) bus.publish(TOPIC, null);
  }

  #closeWhenActive(value) {
    if (value) this.#closeOwn();
  }

  #schedule(panelId) {
    this.#cancel();
    this.#timer = setTimeout(() => {
      this.#timer = 0;
      if (panelId == null) {
        this.#closeOwn();
        return;
      }
      this.#openedBy = 'hover';
      bus.publish(TOPIC, panelId);
    }, HOVER_DELAY);
  }

  #cancel() {
    clearTimeout(this.#timer);
    this.#timer = 0;
  }

  #onClick(event) {
    const tabButton = event.target.closest(TAB_SELECTOR);
    if (tabButton && this.contains(tabButton)) {
      this.#toggle(tabButton, event);
      return;
    }
    if (event.target.closest('[data-desktop-menu-scrim]')) this.#closeOwn();
  }

  #toggle(tabButton, event) {
    this.#cancel();
    const panelId = tabButton.getAttribute('aria-controls');
    if (this.#active !== panelId) {
      this.#openedBy = event.detail === 0 ? 'key' : 'click';
      bus.publish(TOPIC, panelId);
      return;
    }
    if (this.#openedBy === 'hover') {
      this.#openedBy = 'click';
      return;
    }
    bus.publish(TOPIC, null);
  }

  #onPointerDown() {
    if (this.#openedBy === 'key') this.#openedBy = 'click';
  }

  #onPointerOver(event) {
    if (event.pointerType !== 'mouse') return;

    const tabButton = event.target.closest(TAB_SELECTOR);
    if (tabButton) {
      const panelId = tabButton.getAttribute('aria-controls');
      if (panelId === this.#active) this.#cancel();
      else this.#schedule(panelId);
      return;
    }

    if (!this.#owns(this.#active)) {
      this.#cancel();
      return;
    }
    if (this.#openedBy === 'key') {
      this.#cancel();
      return;
    }
    if (this.#panel(this.#active)?.contains(event.target)) this.#cancel();
    else this.#schedule(null);
  }

  #onPointerLeave(event) {
    if (event.pointerType !== 'mouse') return;
    if (!this.#owns(this.#active)) {
      this.#cancel();
      return;
    }
    if (this.#openedBy === 'key') this.#cancel();
    else this.#schedule(null);
  }

  #onFocusOut(event) {
    const next = event.relatedTarget;
    if (!next) return;
    if (!this.contains(next)) {
      this.#closeOwn();
      return;
    }
    if (
      this.#openedBy === 'key' &&
      next.matches(TOP_ITEM) &&
      next !== this.#tabFor(this.#active)
    ) {
      this.#closeOwn();
    }
  }

  #onDocumentClick(event) {
    if (!this.contains(event.target)) this.#closeOwn();
  }

  #onKeydown(event) {
    if (event.key !== 'Escape' || !this.#owns(this.#active)) return;
    const tabButton = this.#tabFor(this.#active);
    const hadFocus = this.contains(document.activeElement);
    this.#closeOwn();
    if (hadFocus) tabButton?.focus();
  }
}

if (!customElements.get('desktop-menu'))
  customElements.define('desktop-menu', DesktopMenu);
